import type { TokenStats } from '../lib/tokenAge';
import type {
  SessionListResponse,
  V2Response,
  HistoryResponse,
  TagsResponse,
  SessionTag,
  ResumeAtTagResult,
  PoolsResponse,
  RbacConfig,
  RbacAssignment,
  BotHealthResponse,
  HistorySearchResponse,
  AgentState,
  ActivityResponse,
  AgentLaneStatus,
  GoalListItem,
  GoalDetail,
  GoalIssue,
  DispatchQueue,
  IntakeResponse,
  QueuePosition,
  QueueConflict,
  QueueReorderResult,
} from './types';
import { serviceOrigin } from '../lib/services';
import { instanceReady } from '../hooks/useInstanceMode';

const BASE = '';
const AUTH_STORAGE_KEY = 'ark-auth';
// issue-api is called DIRECTLY from the FE for the Goal layer (#1257) — Cloud
// deliberately does not proxy it via cloud-api. Same user bearer; needs issue-api
// CORS to allow this origin (server-side).
// Zur AUFRUFZEIT auflösen (nie einfrieren): die Instanz-Map kommt asynchron.
// `null` = Dienst existiert auf dieser Instanz nicht → Aufrufer lassen ihr
// Feature leise weg (Vertrag 77eb0c9).
const issueApi = () => { const o = serviceOrigin('issue'); return o ? `${o}/api/v1` : null; };

export function authHeader(): Record<string, string> {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return {};
    const stored = JSON.parse(raw);
    return stored?.access_token ? { Authorization: `Bearer ${stored.access_token}` } : {};
  } catch {
    return {};
  }
}

// Reactive token refresh (Alex 2026-07-01: "Token expired" red toast when the
// Prompt-Agent submitted). The proactive refresh in useAuth runs ~60s before
// expiry, but on a flaky connection that refresh can be missed — then the next
// API call 401s with no recovery. So when a call gets a 401, swap in a fresh
// access token via the stored refresh_token and retry ONCE. One shared in-flight
// refresh (dedup) so a burst of 401s doesn't hammer auth-api.
const authApi = () => serviceOrigin('auth');

/**
 * „Die Sitzung ist wirklich zu Ende" — ein Signal, kein Fehlertext.
 *
 * Anlass (19.08., Tommy): Nach dem Upload-Gate scheiterten seine Bild-Uploads
 * mit dem nackten Endpunkt-Text „authentication required", dreimal
 * hintereinander wiederholt. Für ihn war das nicht handlungsleitend — er konnte
 * daraus nicht ableiten, dass er sich neu anmelden muss. Vorher fiel es nicht
 * auf, weil die Route offen war: Er war vermutlich längst ohne gültige Sitzung
 * unterwegs, und niemand hat es gemerkt.
 *
 * Deshalb: Scheitert die Token-Erneuerung nach einem 401 (kein Refresh-Token
 * oder abgelehnt), ist das kein Aufruf-Fehler mehr, sondern ein Zustand des
 * Portals. `useAuth` hört hier zu, räumt die Anmeldung ab und zeigt den
 * Anmeldebildschirm — statt jede Aktion einzeln scheitern zu lassen.
 */
const authExpiredListeners = new Set<() => void>();

export function onAuthExpired(fn: () => void): () => void {
  authExpiredListeners.add(fn);
  return () => { authExpiredListeners.delete(fn); };
}

function notifyAuthExpired() {
  authExpiredListeners.forEach((l) => { try { l(); } catch { /* ein Hörer darf den Rest nicht reißen */ } });
}

let refreshInFlight: Promise<boolean> | null = null;
async function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : null;
        if (!stored?.refresh_token) { notifyAuthExpired(); return false; }
        await instanceReady(); // Boot-Barriere (Tenant-Grenze, Cloud 17.08.)
        const authBase = authApi();
        if (!authBase) return false;
        const res = await fetch(`${authBase}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        });
        // Refresh-Token selbst ungültig/abgelaufen → echte Abmeldung, nicht nur
        // ein gescheiterter Aufruf.
        if (!res.ok) { notifyAuthExpired(); return false; }
        const data = await res.json();
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: Date.now() + data.expires_in * 1000,
        }));
        return true;
      } catch {
        return false;
      }
    })().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function readErrorBody(res: Response): Promise<string> {
  try {
    const txt = await res.text();
    if (!txt) return '';
    try {
      const j = JSON.parse(txt);
      // cloud-api uses `error` for hardened endpoints (e.g. /effective-prompt
      // after Cloud commit 1da30b7). FastAPI default is `detail`, custom
      // handlers may use `message`. Fall through to raw text last.
      return j.error || j.message || j.detail || txt;
    } catch {
      return txt;
    }
  } catch {
    return '';
  }
}

// Tags are node-local. An agent the connected node doesn't own — a remote-node
// agent (e.g. arkserver's "Storage") that isn't in this node's session registry
// — makes /api/sessions/{name}/tags return 404 "agent not found". That's not a
// failure: it just has no tags *here*. Swallow the 404 as empty AND remember it,
// so AgentDetail's reloads don't re-fire the identical 404 every time you view
// that agent (Alex's recurring /sessions/Storage/tags 404, 2026-07-01). First
// view still logs one browser-level 404 line (unavoidable); after that it's cached.
const noTagsHere = new Set<string>();
async function fetchTags(name: string): Promise<TagsResponse> {
  if (noTagsHere.has(name)) return { tags: [] };
  const res = await fetch(`${BASE}/api/sessions/${name}/tags`, {
    headers: { 'X-Client-Build': __BUILD_ID__, ...authHeader() },
  });
  if (res.status === 404) { noTagsHere.add(name); return { tags: [] }; }
  if (!res.ok) throw new Error((await readErrorBody(res)) || `API ${res.status}: /api/sessions/${name}/tags`);
  return res.json();
}

// Ein unbekannter /api-Pfad antwortet auf diesem Origin NICHT mit 404, sondern
// mit 200 und der SPA-Startseite (gemessen 2026-08-06: /api/gibt-es-nicht-xyz
// → 200 text/html). Ohne diese Prüfung hält der Client das für Erfolg und
// scheitert erst beim Parsen mit „Unexpected token '<'" — eine Meldung, die
// jeden dazu bringt, den Fehler im eigenen Code zu suchen statt beim fehlenden
// Endpunkt. Cloud ist heute fast darauf hereingefallen (Post #4519).
async function jsonOrExplain<T>(res: Response, path: string): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('text/html')) {
    throw new Error(
      `Endpunkt ${path} existiert auf diesem Knoten nicht (Server lieferte die Startseite statt JSON).`,
    );
  }
  return res.json() as Promise<T>;
}

// X-Client-Build on same-origin cloud-api calls so the running FE version is visible in
// the server log (closes the blind spot behind #141 — nobody could tell which bundle a
// client ran). NOT added to cross-origin issue-api calls: their CORS only allows
// content-type+authorization, so a custom header would fail the preflight.
async function get<T>(path: string): Promise<T> {
  const doFetch = () => fetch(`${BASE}${path}`, { headers: { 'X-Client-Build': __BUILD_ID__, ...authHeader() } });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const msg = await readErrorBody(res);
    // Status MITGEBEN statt ihn im Text zu suchen: Der Fehlertext ist der
    // Server-Body und enthält die Zahl oft gar nicht — ein Aufrufer, der auf
    // „409" im String prüft, entscheidet dann falsch (gefunden 18.08. beim
    // Dialog-Vertrag, bevor es jemand im Feld gemerkt hätte).
    throw Object.assign(new Error(msg || `API ${res.status}: ${path}`), { status: res.status });
  }
  return jsonOrExplain<T>(res, path);
}

/**
 * Datei-Upload über die Portal-Schicht (same-origin `/api/upload`,
 * `/api/upload/avatar`) — MIT Anmeldung und 401-Wiederholung.
 *
 * Anlass (18.08., gemessen und mit Cloud bestätigt): Diese Route hatte KEIN
 * Gate. `POST /api/upload` nahm von jedem im Netz eine Datei an und reichte sie
 * mit dem Schlüssel der Instanz an den Speicher weiter — auf jeder Instanz, die
 * Kundeninstanz eingeschlossen. Cloud setzt das Gate; damit dabei nicht
 * gleichzeitig Anhänge, Diktat-Uploads und Avatare brechen, tragen die
 * Aufrufstellen ab jetzt den Token. Der Header ist auf einer ungegateten Route
 * wirkungslos — er kostet nichts und ist am Tag des Gates schon da.
 *
 * Kein `Content-Type`: Die Grenze des multipart-Körpers setzt der Browser. Und
 * das FormData wird pro Versuch neu gebaut — ein verbrauchter Körper lässt sich
 * bei der 401-Wiederholung nicht erneut senden.
 */
async function uploadFileRaw(path: string, file: File): Promise<Response> {
  const doFetch = () => {
    const form = new FormData();
    form.append('file', file);
    return fetch(`${BASE}${path}`, { method: 'POST', headers: authHeader(), body: form });
  };
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  return res;
}

// Speech-to-text upload. Same authHeader + 401-retry chain as everything else —
// added ahead of Cloud's auth-gate deploy for #534 (the endpoint answered
// unauthenticated until now, so this header was missing). Sending it early is
// free: an ungated endpoint ignores it, and we don't break the second the gate
// lands — the exact failure we had with the usage widget after #483.
//
// NOTE: no explicit Content-Type — the browser must set the multipart boundary
// itself. And the FormData is rebuilt per attempt: a consumed body can't be
// replayed on the 401-retry.
async function transcribeUpload(blob: Blob, filename: string): Promise<TranscribeResponse> {
  const doFetch = () => {
    const form = new FormData();
    form.append('audio', blob, filename);
    return fetch(`${BASE}/api/transcribe`, { method: 'POST', headers: authHeader(), body: form });
  };
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  // The endpoint answers 200 even on failure (text:"" + error), so a non-ok
  // status is a genuine transport/auth problem and worth surfacing as such.
  if (!res.ok) throw new Error(`transcribe ${res.status}`);
  return res.json();
}

// Same-origin DELETE with auth + 401-retry. The file had no `del` helper, so
// every DELETE call site dropped to raw fetch — which is how /api/rbac/assignments
// ended up unauthenticated (#591).
async function del(path: string): Promise<void> {
  const doFetch = () =>
    fetch(`${BASE}${path}`, {
      method: 'DELETE',
      headers: { 'X-Client-Build': __BUILD_ID__, ...authHeader() },
    });
  let res = await doFetch();
  if (res.status === 401 && (await refreshAccessToken())) res = await doFetch();
  if (!res.ok) throw new Error((await readErrorBody(res)) || `API ${res.status}: ${path}`);
}

// Server-side OpenAI TTS. Returns the RAW Response on purpose: the three call
// sites need different parts of it — InputBar reads the X-TTS-Preview /
// X-TTS-Summarized headers and renders server error bodies, lib/tts.ts passes an
// AbortSignal for its timeout budget, MultiAgent only wants the blob. Wrapping it
// in a typed result would have cost that diagnostic detail.
//
// Auth like transcribe (#534): the endpoint still answers unauthenticated today,
// so the header is inert — but it means the gate deploy can't break TTS the way
// #483 broke the usage widget. JSON bodies are replayable, so the same body
// object can be reused for the 401-retry (unlike the FormData above).
export function ttsText(
  body: { text: string; voice: string; lang: string; style?: 'compact' | 'prose' },
  signal?: AbortSignal,
): Promise<Response> {
  const doFetch = () =>
    fetch(`${BASE}/api/tts-text`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify(body),
      signal,
    });
  return doFetch().then(async (res) =>
    res.status === 401 && (await refreshAccessToken()) ? doFetch() : res,
  );
}

export interface TranscribeResponse {
  text?: string;
  audio_path?: string;
  duration_secs?: number;
  cost_usd?: number;
  total_cost_usd?: number;
  /** Present only when transcription itself failed — the HTTP status stays 200,
   *  so this field is the ONLY way to tell "silence" from "it broke". */
  error?: string;
}

// Account-usage (#426) lives on arkserver ONLY — it needs the Anthropic master
// tokens, and on mac the route doesn't exist at all (the request falls through
// to the SPA index and returns HTML). So it's addressed absolutely instead of
// via same-origin BASE. Auth is REQUIRED since Cloud's a44a04b (#483) — before
// that it answered unauthenticated to the public internet. Like issue-api this
// sends only Authorization: the endpoint's CORS allow-headers is `authorization`
// alone, so adding X-Client-Build would fail the preflight.
// Registry-gebunden (Alex 17.08., Screenshot 119751): Davids Standalone-Portal
// zeigte ALEX' und JASCHAS Konten-Auslastung — die Konstante zeigte absolut auf
// den Betreiber-arkserver, an der Dienst-Registry vorbei (13. Fund dieser
// Klasse). Jetzt zur Aufrufzeit; auf standalone ist der Pfad ohnehin
// abgeschaltet (SessionList gated das Widget am Instanz-Modus).
const usageApi = () => serviceOrigin('usage');
async function usageGet<T>(path: string): Promise<T> {
  const base = usageApi();
  if (!base) throw new Error('usage_service_unavailable');
  const doFetch = () => fetch(`${base}${path}`, { headers: authHeader() });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) throw new Error(`account-usage ${res.status}`);
  return res.json();
}

// What counts as an unprocessed issue, federation-wide. Single source of truth for
// the list badge, the header button and the per-agent panel — they drifted apart
// once and produced three different numbers for the same agent.
export const OPEN_ISSUE_STATUSES = ['open', 'in_progress', 'deferred'] as const;

// Page through an issue-api query until a short page arrives. `query` must already
// carry its own params (no leading `&`); limit/offset are appended here.
const ISSUE_PAGE = 100;
async function issuePages<T>(query: string): Promise<T[]> {
  const out: T[] = [];
  // Hard stop at 50 pages (5000 records): a backend that ignored `offset` would
  // otherwise loop forever handing back the same first page.
  for (let page = 0; page < 50; page++) {
    const r = await issueGet<{ items?: T[] }>(`${query}&limit=${ISSUE_PAGE}&offset=${page * ISSUE_PAGE}`);
    const items = r.items || [];
    out.push(...items);
    if (items.length < ISSUE_PAGE) break;
  }
  return out;
}

// Same user bearer, absolute issue-api origin (Goal layer reads issue-api directly).
async function issueGet<T>(path: string): Promise<T> {
  await instanceReady();
  const base = issueApi();
  if (!base) throw new Error('issue_service_unavailable');
  const doFetch = () => fetch(`${base}${path}`, { headers: authHeader() });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const msg = await readErrorBody(res);
    throw new Error(msg || `issue-api ${res.status}: ${path}`);
  }
  return res.json();
}
async function issueSend<T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  await instanceReady();
  const base = issueApi();
  if (!base) throw new Error('issue_service_unavailable');
  const doFetch = () => fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: body ? JSON.stringify(body) : undefined,
  });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const msg = await readErrorBody(res);
    throw new Error(msg || `issue-api ${res.status}: ${path}`);
  }
  return res.json().catch(() => ({} as T));
}

/**
 * Prompt-Agent-Eingang. Zwei Pfade, EIN Client — kein instanzabhängiger Fork.
 *
 * `/api/v1/intake` ist der Sammel-Eingang: Freitext hinein, der Dienst
 * entscheidet Issue, Goal oder Chat. Auf einer frisch bereitgestellten
 * Kundeninstanz gibt es ihn noch nicht — Davids issue-api führt heute 14
 * Routen, darunter `/issues/intake` und `/goals/intake`, aber nicht den
 * Sammel-Eingang (gemessen 19.08.: `/api/v1/intake` → 404, `/issues/intake`
 * → 401, also vorhanden und nur ungeprüft).
 *
 * Deshalb bei GENAU 404 der zweite Versuch auf `/issues/intake`. Beide nehmen
 * dasselbe Anfrage-Schema; die Antwort dort ist issue-förmig (ohne `intent`),
 * was die Anzeige korrekt als „Issue #N" liest. Jeder andere Fehler wird
 * durchgereicht — ein 500 darf nicht als „dann eben Issue" durchrutschen.
 */
async function intakeSubmit<T>(body: unknown): Promise<T> {
  await instanceReady();
  const base = issueApi();
  if (!base) throw new Error('issue_service_unavailable');
  const send = (path: string) => fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
  });
  let res = await send('/intake');
  if (res.status === 401 && await refreshAccessToken()) res = await send('/intake');
  if (res.status === 404) {
    res = await send('/issues/intake');
    if (res.status === 401 && await refreshAccessToken()) res = await send('/issues/intake');
  }
  if (!res.ok) {
    const msg = await readErrorBody(res);
    throw new Error(msg || `issue-api ${res.status}: intake`);
  }
  return res.json().catch(() => ({} as T));
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const doFetch = () => fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Client-Build': __BUILD_ID__, ...authHeader() },
    body: body ? JSON.stringify(body) : undefined,
  });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const msg = await readErrorBody(res);
    throw new Error(msg || `API ${res.status}: ${path}`);
  }
  return jsonOrExplain<T>(res, path);
}

// Direct-node JSON mutation for the few legacy routes that are not yet
// federation-forwarded by cloud-api (currently /switch-agent, #565).  Remote
// node URLs are the currently deployed browser workaround; adding our bearer
// + X-Client-Build there would widen the CORS preflight and breaks peers whose
// allow-header list is intentionally narrow. Peers behind server-side Basic
// Auth still require the forwarding fix tracked in #565. Same-origin calls
// keep the normal authenticated headers + one reactive token refresh.
async function jsonMutation<T>(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body: unknown,
  nodeBase = '',
): Promise<T> {
  const remote = !!nodeBase;
  const base = nodeBase.replace(/\/$/, '');
  const doFetch = () => fetch(`${base}${path}`, {
    method,
    headers: remote
      ? { 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json', 'X-Client-Build': __BUILD_ID__, ...authHeader() },
    body: JSON.stringify(body),
  });
  let res = await doFetch();
  if (!remote && res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const msg = await readErrorBody(res);
    throw new Error(msg || `API ${res.status}: ${path}`);
  }
  return res.json();
}


/** Kennung je Absendung. `randomUUID` gibt es nur im sicheren Kontext — alle
 *  ausgelieferten Ursprünge sind https, der Rückfall deckt den Rest ab. */
function newMessageId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* Rückfall unten */ }
  return `cmid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const api = {
  sessions: {
    all: () => get<SessionListResponse>('/api/sessions/all'),
    remote: () => get<SessionListResponse>('/api/sessions/remote'),
    v2: (name: string, lines = 500, lastHash?: string) => {
      let url = `/api/sessions/${name}/v2?lines=${lines}`;
      if (lastHash) url += `&last_hash=${lastHash}`;
      return get<V2Response>(url);
    },
    // Transcript-backed history (Cloud 2026-06-23, #1204). `before` empty =
    // newest page; pass a prior `next_cursor` (uuid, opaque) for older turns.
    history: (name: string, before?: string | null, limit = 50) => {
      let url = `/api/sessions/${name}/history?limit=${limit}`;
      if (before) url += `&before=${encodeURIComponent(before)}`;
      return get<HistoryResponse>(url);
    },
    // `client_message_id` je Absendung — der Endpunkt liest sie seit #489
    // optional aus und macht damit eine Wiederholung ungefaehrlich: Bricht die
    // Verbindung nach dem Absenden ab, liefert derselbe Aufruf nicht zweimal.
    // Der Client hat sie bis heute nie mitgeschickt; die Zusage lag brach.
    //
    // Zweiter Zweck (Cloud, 13.08.): Sie ist der einzige Schluessel, ueber den
    // sich ein angezeigter Zug wieder mit dem AUTHENTIFIZIERTEN Absender
    // verbinden laesst. Der Zug entsteht im Parser aus dem tmux-Abzug und hat
    // dort keine Identitaet; `input_dedup` haelt sie fest, aber nur zu dieser
    // Kennung. Ohne sie bliebe die geplante Urheber-Anzeige fuer JEDE Nachricht
    // aus diesem Portal leer — gebaut und wirkungslos.
    //
    // Pro Aufruf neu, nicht pro Text: Wer zweimal „ok" schickt, meint zweimal.
    // Die 401-Wiederholung in `post()` benutzt denselben Rumpf und damit
    // dieselbe Kennung — genau so ist die Zusage gemeint.
    sendInput: (name: string, text: string) =>
      post('/api/sessions/' + name + '/input', {
        type: 'line',
        data: text,
        client_message_id: newMessageId(),
      }),
    sendKey: (name: string, key: string) =>
      post('/api/sessions/' + name + '/input', { type: 'key', key }),
    /**
     * Antwort auf einen Dialog (Freigabe/Menü). `send` kommt vom Server —
     * Tastendruck ODER ganze Zeile (ConPTY-Codex). `promptId` ist der
     * Fingerabdruck des angezeigten Dialogs: Passt er serverseitig nicht
     * mehr, kommt 409 statt einer verirrten Eingabe in eine fremde Frage
     * (Vertrag mit Cloud, 18.08.).
     */
    answerPrompt: (
      name: string,
      send: { type: 'key'; key: string } | { type: 'line'; text: string },
      promptId?: string,
    ) =>
      post('/api/sessions/' + name + '/input',
        send.type === 'key'
          ? { type: 'key', key: send.key, ...(promptId ? { prompt_id: promptId } : {}) }
          : { type: 'line', data: send.text, client_message_id: newMessageId(), ...(promptId ? { prompt_id: promptId } : {}) }),
    create: (body: {
      name?: string;
      agent?: string;
      model?: string;
      effort?: string;
      owner_email?: string;
      node?: string;
      department?: string;
      role?: string;
      auto_restart?: boolean;
      tenant_id?: string;
      description?: string;
      chrome_profile?: string;
    }) =>
      post<{ name: string }>('/api/sessions', body),
    // Was a raw unauthenticated DELETE until #591 — Cloud verified from outside
    // that it reached the handler without a token (only 404 because the name
    // didn't exist; a real one would have been deleted). Gated since 301ff03.
    // Now throws on failure instead of resolving with a non-ok Response, so a
    // rejected kill can no longer look like a successful one; all four call
    // sites already sit in try/catch.
    kill: (name: string) => del(`/api/sessions/${name}`),
    restart: (name: string, opts?: { wipe_history?: boolean }) =>
      post(`/api/sessions/${name}/restart`, opts ?? {}),
    suspend: (name: string) =>
      post(`/api/sessions/${name}/suspend`),
    resume: (name: string) =>
      post(`/api/sessions/${name}/resume`),
    // Change the CLI provider of an existing agent while preserving its
    // identity/persona and (optionally) handing over a compacted context file.
    // Unlike lifecycle/config routes this endpoint is not federation-forwarded
    // yet (#565), so callers must pass SessionInfo.node_url for remote owners.
    switchAgent: (
      name: string,
      body: { agent: 'claude' | 'codex' | 'agy'; model: string; carry_context: boolean },
      nodeUrl = '',
    ) => jsonMutation<{
      ok: boolean;
      name: string;
      from: string;
      to: string;
      model: string;
      handoff_written: boolean;
      handoff?: { chars?: number; tail_turns?: number; had_summary?: boolean; error?: string };
      wake?: { error?: string };
    }>('POST', `/api/sessions/${encodeURIComponent(name)}/switch-agent`, body, nodeUrl),
    // Config PATCH is federation-forwarded server-side. #562 uses it to
    // normalise effort before switching until /switch-agent accepts effort
    // atomically (#565).
    updateConfig: (name: string, body: { effort?: string }) =>
      jsonMutation<Record<string, unknown>>(
        'PATCH',
        `/api/sessions/${encodeURIComponent(name)}/config`,
        body,
      ),
    // Cold-start / wake via the `ensure_live` primitive (Cloud, session_routes
    // :290). One call for ALL non-live states: running→no-op, suspended→resume
    // (--continue), orphan→register + tmux cold-start. Federation-aware (local
    // first, else forward to owning node). SYNCHRONOUS — returns when the agent
    // is running + login-checked (~12 s for an orphan cold-start). `actions[0]`
    // ∈ already_live | resumed | cold_started | skipped_no_autostart.
    wake: (name: string) =>
      post<{ name: string; actions?: string[]; error?: string }>(`/api/sessions/${name}/wake`),
    // History-Tags (Cloud 2026-06-23, #1204 follow-up). A tag is a named
    // bookmark on a transcript turn (`turn_uuid` from /history). Federation-
    // forwarded like /v2 + /history. resumeAtTag rewinds the agent to the
    // tagged turn NON-destructively (branches the .jsonl + restarts on the
    // branch); clearResumeTag un-pins → restart back to the full history.
    tags: (name: string) => fetchTags(name),
    addTag: (name: string, tag: string, turnUuid: string) =>
      post<{ ok: boolean; tag: SessionTag }>(`/api/sessions/${name}/tags`, { tag, turn_uuid: turnUuid }),
    deleteTag: (name: string, tag: string) =>
      fetch(`${BASE}/api/sessions/${name}/tags/${encodeURIComponent(tag)}`, {
        method: 'DELETE',
        headers: authHeader(),
      }),
    resumeAtTag: (name: string, tag: string) =>
      post<ResumeAtTagResult>(`/api/sessions/${name}/resume-at-tag`, { tag }),
    // Clone a session's full config under a new name (Cloud /clone, federated).
    // clone_history=true deep-copies the transcript so the clone's --continue
    // resumes the source conversation; false = fresh. ephemeral+ttl_hours =
    // throwaway. `from_tag` (branch-at-tag clone) is pending a Cloud backend
    // addition — omit until supported (additionalProperties:true would else
    // silently swallow it).
    clone: (name: string, body: {
      new_name: string;
      new_owner?: string;
      clone_history?: boolean;
      ephemeral?: boolean;
      ttl_hours?: number;
      start?: boolean;
      // from_tag: branch the clone's conversation at a history tag (implies
      // clone_history). node/target: clone then relocate to that node in one
      // call (reuses /migrate's hard verify-gate). Both LIVE (Cloud 4ffd217).
      from_tag?: string;
      node?: string;
    }) => post<{ name?: string; node?: string; from_tag?: string; error?: string }>(`/api/sessions/${name}/clone`, body),
    // MOVE an agent to another federation node (Cloud /migrate). NOT a copy —
    // suspend → rsync bot-home → register+start on target → HARD verify (5×) →
    // delete on source. Cloud closed a data-loss gap: a failed verify now
    // ABORTS with the source intact + resumed (status='aborted_source_intact'),
    // never deletes the only copy unconfirmed. Read `status`, NOT ok/node.
    // Slow for large homes (494 MB ≈ 8 min) → nginx may 504 while the move
    // still finishes server-side; treat a thrown 504/timeout as "still running".
    migrate: (name: string, target: string) =>
      post<{
        name?: string;
        source?: string;
        target?: string;
        steps?: string[];
        status?: 'migrated' | 'aborted_source_intact' | 'failed';
        home_bytes?: number;
        error?: string;
        warning?: string;
      }>(`/api/sessions/${name}/migrate`, { target }),
    clearResumeTag: (name: string) =>
      post<ResumeAtTagResult>(`/api/sessions/${name}/resume-at-tag`, { clear: true }),
    reset: (
      name: string,
      body: { instruction?: string; max_wait?: number; force_cloud?: boolean } = {},
    ) =>
      post<{
        name: string;
        wiped_files: number;
        reset_ok_confirmed: boolean;
        memory_backup: string;
        phases: string[];
        final_state: string;
        error?: string;
      }>(`/api/sessions/${name}/reset`, body),
    getMcp: (name: string) =>
      get<{ name: string; servers: Record<string, { url: string }>; source: string }>(
        `/api/sessions/${name}/mcp-servers`
      ),
    memory: (name: string) =>
      get<{
        name: string;
        projects: Array<{
          project_id: string;
          files: Array<{ name: string; size: number; content: string; is_index?: boolean }>;
        }>;
      }>(`/api/sessions/${name}/memory`),
    effectivePrompt: (name: string) =>
      get<{
        name: string;
        agent: string;
        instruction_file: string;
        rendered: string;
        // Three lists at top-level per Cloud's ef40eb9 (verified live
        // 2026-06-06). `disabled_keys` is the merged union. `bot_*` and
        // `pool_*` are the scoped sources — drive the per-scope sidebar.
        disabled_keys?: string[];
        bot_disabled_keys?: string[];
        pool_disabled_keys?: string[];
        pool_name?: string;
        layers: Array<{
          source: string;
          path: string;
          content: string;
          // Hierarchical fragment metadata — present on system/mcp/node/tenant/
          // pool/bot fragments. Backend already supplies the section heading
          // and post id, so the inspector can label each row without a click.
          section?: string;
          title?: string;
          scope_type?: string;
          scope_key?: string;
          post_id?: number;
          // Identifies the fragment for toggle-API calls. Disabled fragments
          // become ghost layers (rendered struck-through). `disabled_by`
          // tells the inspector whether the toggle lives in bot- or pool-
          // scope when the user wants to re-enable.
          canonical_key?: string;
          disabled?: boolean;
          disabled_by?: 'bot' | 'pool';
        }>;
      }>(`/api/sessions/${name}/effective-prompt`),
    // Toggle individual prompt-fragments off for this specific bot.
    // Body shape: { canonical_keys: ["federation-collab-operating-mode", …] }.
    // Sending an empty array clears the per-bot disabled list (= reset).
    // Effect is visible on the bot's NEXT prepare_bot_home / restart.
    setDisabledFragments: (name: string, canonicalKeys: string[]) =>
      post<{ name: string; disabled: string[] }>(
        `/api/sessions/${name}/prompt-fragments/disabled`,
        { canonical_keys: canonicalKeys },
      ),
    // Phase-2 collection-subscription endpoints (Cloud spec'd in Post 995
    // q-349a3ee4e131). Bot subscribes to one or more content-api collection
    // ids → applicable_fragments() pulls all prompt_fragment posts in that
    // sub-tree as additional layers between scope=bot and scope=pool.
    getCollectionSubscriptions: (name: string) =>
      get<{ name: string; collection_ids: number[] }>(
        `/api/sessions/${name}/collection-subscriptions`,
      ),
    setCollectionSubscriptions: (name: string, collectionIds: number[]) =>
      post<{ name: string; collection_ids: number[] }>(
        `/api/sessions/${name}/collection-subscriptions`,
        { collection_ids: collectionIds },
      ),
    // Also raw and unauthenticated until #591 — this one answered HTTP 200 from
    // the open internet, i.e. anyone could rewrite a foreign agent's MCP servers.
    // Gated since 301ff03.
    setMcp: (name: string, servers: string[]) =>
      jsonMutation<unknown>('PUT', `/api/sessions/${name}/mcp-servers`, { servers }),
  },
  agents: {
    // Who this agent communicates with most, from the IACP message log
    // (Cloud 2026-06-21). Used to sort the group-chat recipient picker so the
    // likeliest agents are already on top. all-time by default (stable order).
    contacts: (agentName: string, opts?: { days?: number; limit?: number }) => {
      const q = new URLSearchParams();
      if (opts?.days) q.set('days', String(opts.days));
      if (opts?.limit) q.set('limit', String(opts.limit));
      const qs = q.toString();
      return get<{
        agent: string;
        days: number | null;
        contacts: { name: string; total: number; sent: number; received: number; last_at: string }[];
      }>(`/api/agents/${encodeURIComponent(agentName)}/contacts${qs ? `?${qs}` : ''}`);
    },
    // Federation-wide work-status overview (#1251, Cloud builds the endpoint).
    // effective_state is server-merged (live overrides stale declared). Grouped
    // client-side by task_id for the Work-Board bubbles.
    status: () => get<{ agents: AgentLaneStatus[] }>('/api/agents/status'),
  },
  // Goal layer (#1257) — read issue-api directly (no cloud-api proxy). list → goals,
  // detail → issues_summary rollup, issues → the linked IssueRead[]. The FE joins the
  // lane-pulse (api.agents.status, task_id="goal:N") and derives "blocked" from it.
  goals: {
    // GET /goals returns a paginated envelope {items,total,limit,offset} — NOT a bare
    // array. The FE did list.map() → "list.map is not a function" → the whole Goals
    // dashboard showed "nicht erreichbar" despite 24× HTTP 200 (#162). Unwrap .items.
    list: () => issueGet<{ items: GoalListItem[] }>('/goals').then((r) => r.items ?? []),
    detail: (id: number) => issueGet<GoalDetail>(`/goals/${id}`),
    issues: (id: number) => issueGet<GoalIssue[]>(`/goals/${id}/issues`),
  },
  // Back-pressure dispatch queue (#1257 Phase B) — one agent's active + queued work.
  dispatch: {
    queue: (assignee: string) => issueGet<DispatchQueue>(`/dispatch/queue?assignee=${encodeURIComponent(assignee)}`),
    /**
     * Neue Reihenfolge als GANZE Liste setzen. Bewusst kein "move up/down":
     * bei zwei gleichzeitig sortierenden Clients divergieren Einzelschritte
     * still, eine vollständige Liste kann der Server dagegen gegen den
     * Ist-Zustand prüfen.
     *
     * Wirft NICHT bei 409, sondern gibt den Konflikt zurück — `queue_set_mismatch`
     * liefert `expected_issue_ids` in aktueller Reihenfolge mit, womit die
     * Oberfläche sofort neu zeichnen kann, statt einen GET nachzuschieben.
     */
    reorder: async (assignee: string, issueIds: number[]): Promise<QueueReorderResult> => {
      const base = issueApi();
      if (!base) throw new Error('issue_service_unavailable');
      const url = `${base}/dispatch/queue/${encodeURIComponent(assignee)}/order`;
      const doFetch = () => fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ issue_ids: issueIds }),
      });
      let res = await doFetch();
      if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
      if (res.ok) return { ok: true, snapshot: await res.json() as DispatchQueue };
      if (res.status === 409) {
        // FastAPI verpackt HTTPException-Details in `detail`. Live gegen prod
        // geprüft: {"detail":{"error":"active_issue_not_reorderable",…}}. Ohne
        // das Auspacken wäre `error` undefined und JEDER Konflikt liefe in den
        // generischen Zweig — der Nutzer bekäme „unbekannter Fehler" statt der
        // brauchbaren Meldung, und bei `queue_set_mismatch` ginge die
        // mitgelieferte Ist-Reihenfolge verloren.
        const body = await res.json() as QueueConflict | { detail: QueueConflict };
        return { ok: false, conflict: ('detail' in body ? body.detail : body) as QueueConflict };
      }
      throw new Error(await readErrorBody(res) || `issue-api ${res.status}`);
    },
  },
  // Smart-intake — free text → DeepSeek classifies + creates Goal/Issue (or chats).
  // contextAgent (bare name) = the chat the prompt came from; the endpoint defaults the
  // assignee to it unless the text explicitly delegates ("für X"), so no guessing (#147).
  // „Wie lange war der eingeloggt?" am Login-Fehler (Alex 2026-08-07).
  // Bewusst NUR auf Abruf, nicht im Poll: die Zahl interessiert genau dann,
  // wenn jemand vor einem blockierten Agenten steht.
  tokenStats: (name: string) => get<TokenStats>(`/api/token-stats/agent/${encodeURIComponent(name)}`),
  intake: {
    // `queuePosition` (Issue #943): "front" reiht das neue Issue VOR die
    // wartenden ein, statt es hinten anzuhängen. Default bleibt bewusst "back"
    // — die Bedeutung bestehender Aufrufe darf sich nicht still ändern.
    submit: (text: string, contextAgent?: string, queuePosition?: QueuePosition) =>
      intakeSubmit<IntakeResponse>({
        text,
        ...(contextAgent ? { context_agent: contextAgent } : {}),
        ...(queuePosition ? { queue_position: queuePosition } : {}),
      }),
  },
  // Per-agent issue list + actions (Alex 2026-06-29: see/act on an agent's open work).
  issues: {
    // Both reads below filter by status SERVER-side and page through the result.
    //
    // They used to fetch a fixed newest-N slice of ALL statuses and filter to open
    // ones in the browser — which silently undercounted, because "newest N records"
    // is not "the open ones". Measured 2026-07-31 against prod: 581 issues total,
    // 122 genuinely open (66 open + 6 in_progress + 50 deferred), but the newest-200
    // window contained only 42 of them. The badge hid 80 open issues (66%), hitting
    // `deferred` hardest — deferred work is by definition old, so it falls out of a
    // recency window first. Same defect one level down: `forAssignee` took the newest
    // 50 of any status while e.g. Cloud has 59 assigned records.
    //
    // PAGE=100 because issue-api returns 0 rows for limit>~250 (cap bug) — paging in
    // 100s stays well clear of it and makes the cap irrelevant instead of load-bearing.
    //
    // EIN Aufruf statt drei, seit issue-api `status` als Kommaliste nimmt
    // (12.08.). Die Schleife hier war der Grund, warum jede Ansicht dreimal
    // fragte — nicht die Taktrate. Die Liste zeigt echte Vorgänge, hier lässt
    // sich also nichts weiter zusammenfassen; die Zählung sitzt in `counts`.
    forAssignee: async (assignee: string) => {
      const items = await issuePages<GoalIssue>(
        `/issues?assignee=${encodeURIComponent(assignee)}&status=${OPEN_ISSUE_STATUSES.join(',')}`);
      return { items, total: items.length };
    },
    // Open-issue count per agent (#180) → blue list badge. Same status set as the #177
    // header button: a dispatched issue flips to in_progress immediately, so filtering
    // on `open` alone would drop exactly the issue being worked on right now.
    //
    // Serverseitig gezählt seit 2026-08-12. Vorher holte diese Stelle je Status
    // ALLE offenen Vorgänge seitenweise und zählte im Browser — 187.893 Bytes
    // gegen 722, und das dreimal statt einmal. Cloud mass 8206 Listenabrufe in
    // 24 Stunden; der größte Teil davon war diese Form.
    //
    // Issue hat dabei eine Falle in `queued` behoben, die ich nicht gesehen
    // hätte: 13 rohe Queue-Einträge, davon 0 tatsächlich zustellbar (12
    // zurückgestellt, 1 erledigt). Das Abzeichen hätte 13 Wartende angezeigt,
    // wo es keine gibt. Der Endpunkt zählt nur, was wirklich promotet wird.
    openCounts: async () => {
      const raw = await issueGet<Record<string, number>>(
        `/issues/counts?group_by=assignee&status=${OPEN_ISSUE_STATUSES.join(',')}`);
      const m = new Map<string, number>();
      for (const [key, n] of Object.entries(raw || {})) {
        const a = key.replace(/^agent:/, '');
        if (a && typeof n === 'number') m.set(a, n);
      }
      return m;
    },
    /** Zahlen EINES Agenten — offene Vorgänge und tatsächlich wartende
     *  Zustellungen. Ersetzt drei seitenweise Abfragen durch einen Aufruf. */
    countsFor: (assignee: string) =>
      issueGet<Record<string, number>>(`/issues/counts?assignee=${encodeURIComponent(assignee)}`),
    // Selbstdiagnose (#1035, Alex): der Realtime-Agent legt seinen eigenen
    // Fehlschlag ab, statt ihn nur auszusprechen. Bewusst dieselbe Nutzer-
    // Anmeldung wie die Lesewege oben — ein Bericht ohne Urheber wäre in einer
    // föderationsweiten Liste wertlos.
    create: (body: {
      title: string;
      description: string;
      component: string;
      severity: string;
      reporter: string;
      reporter_type: string;
      tags?: string[];
      metadata_json?: Record<string, unknown>;
    }) => issueSend<GoalIssue>('POST', '/issues', body),
    comment: (id: number, body: string) =>
      issueSend<{ id: number }>('POST', `/issues/${id}/comments`, { body }),
    // Offene Einträge einer Komponente — Grundlage der Zusammenführung über den
    // Abdruck. Bewusst NUR offene: ein Defekt, der nach dem Schließen wieder
    // auftritt, ist ein Rückfall und verdient einen eigenen Eintrag, keinen
    // Kommentar an einer bereits beantworteten Frage.
    openByComponent: async (component: string) => {
      const items: GoalIssue[] = [];
      for (const status of OPEN_ISSUE_STATUSES) {
        items.push(...await issuePages<GoalIssue>(
          `/issues?component=${encodeURIComponent(component)}&status=${status}`));
      }
      return items;
    },
    resolve: (id: number, body: { commit: string; summary: string; status?: string }) =>
      issueSend<GoalIssue>('POST', `/issues/${id}/resolve`, body),
    update: (id: number, body: Record<string, unknown>) =>
      issueSend<GoalIssue>('PATCH', `/issues/${id}`, body),
  },
  pools: {
    all: () => get<PoolsResponse>('/api/pools/all'),
    // Pool-side effective-prompt mirrors the bot endpoint shape. Returns
    // the merged template a fresh pool member would render with, plus
    // `pool_disabled_keys` driving the pool-scope toggles. Cloud's commit
    // ef40eb9.
    effectivePrompt: (poolName: string) =>
      get<{
        name: string;
        pool_name?: string;
        pool_disabled_keys?: string[];
        disabled_keys?: string[];
        layers: Array<{
          source: string;
          path: string;
          content: string;
          section?: string;
          title?: string;
          scope_type?: string;
          scope_key?: string;
          post_id?: number;
          canonical_key?: string;
          disabled?: boolean;
          disabled_by?: 'bot' | 'pool';
        }>;
      }>(`/api/pools/${poolName}/effective-prompt`),
    // Pool-wide fragment-toggle. Same body shape as the bot variant.
    // Mutates `pool_config.disabled_fragments` (server-side).
    setDisabledFragments: (poolName: string, canonicalKeys: string[]) =>
      post<{ name: string; disabled: string[] }>(
        `/api/pools/${poolName}/prompt-fragments/disabled`,
        { canonical_keys: canonicalKeys },
      ),
    // Pool-scope collection subscriptions — symmetric to the bot endpoint.
    // Cloud's IACP fd979e1b: pool-level subs are the primary source,
    // instance-level can only extend (additive union). When a session is a
    // pool member, the FE should write here instead of to the per-session
    // endpoint so changes apply pool-wide. Discovered via `pool_name` on
    // session-info; standalone bots stay on the per-session endpoint.
    getCollectionSubscriptions: (poolName: string) =>
      get<{ name: string; collection_ids: number[] }>(
        `/api/pools/${poolName}/collection-subscriptions`,
      ),
    setCollectionSubscriptions: (poolName: string, collectionIds: number[]) =>
      post<{ name: string; collection_ids: number[] }>(
        `/api/pools/${poolName}/collection-subscriptions`,
        { collection_ids: collectionIds },
      ),
  },
  agentStates: () =>
    get<{ states: Record<string, AgentState> }>('/api/agent-states'),
  // Agent River activity history (Cloud Phase-1, IACP 6a570eae). Sparse
  // per-agent pane-change buckets. Per-node like /api/sessions/all, with
  // a /remote variant fanning out to peers. `bucket` in seconds, clamped
  // [15,3600] server-side and snapped to the 30s base; the returned
  // `bucket` is the effective value. `state` is empty in the MVP — the FE
  // live-edge fills runtime state from SSE.
  activity: (since: number, until: number, bucket = 30) =>
    get<ActivityResponse>(`/api/activity?since=${since}&until=${until}&bucket=${bucket}`),
  activityRemote: (since: number, until: number, bucket = 30) =>
    get<ActivityResponse>(`/api/activity/remote?since=${since}&until=${until}&bucket=${bucket}`),
  // Login profile catalog (Cloud commit a97bd76). Replaces the hardcoded
  // Profile 3/4/6/20 list — exposes per-profile provider (anthropic /
  // openai / gemini) plus the signed-in email + aliases mapping. Used by
  // the Edit-Agent dialog's chrome_profile dropdown (filtered by provider
  // matching agent type) and by useBotHealth's account resolver.
  loginProfiles: () =>
    get<{
      profiles: Array<{
        profile: string;
        provider: 'anthropic' | 'openai' | 'gemini' | string;
        // null when the catalog has a profile slot registered but no
        // one has actually logged in yet (= no signed_in_as resolved
        // from the OAuth/cookie state). FE must defensive-guard.
        signed_in_as: string | null;
        aliases: string[];
      }>;
    }>('/api/login/profiles'),
  // Model catalog (Cloud commit eb073e2). The authoritative, registry-
  // derived list of selectable models per agent type — replaces the
  // hardcoded model <option> list in NewSessionDialog so a backend-side
  // addition (new MiniMax/DeepSeek variant, new provider) appears in the
  // dropdown with NO FE deploy. `claude` + `providers` are registry-
  // derived (ANTHROPIC_COMPAT_PROVIDERS); codex/gemini are a backend
  // start-set that may not be registry-backed yet.
  models: () =>
    get<{
      claude: string[];
      codex?: string[];
      gemini?: string[];
      agy?: string[];
      efforts?: {
        [provider: string]: { available?: string[]; default?: string } | undefined;
      };
      defaults?: { [provider: string]: string | undefined };
      supports_model_arg?: string[];
      providers_meta?: {
        [provider: string]: { supports_model_arg?: boolean } | undefined;
      };
      providers?: {
        anthropic_native?: string[];
        minimax?: string[];
        deepseek?: string[];
        [k: string]: string[] | undefined;
      };
    }>('/api/models'),
  /** Whisper STT. `filename` matters: the backend derives the stored file's
   *  suffix from it (`Path(audio.filename).suffix`), so it must match the real
   *  container — see the call site. */
  transcribe: (blob: Blob, filename: string) => transcribeUpload(blob, filename),
  /** Roh-Antwort, weil die drei Aufrufstellen unterschiedliche Teile brauchen
   *  (Fehlertext, JSON, Rückfall auf die generische Route). */
  uploadRaw: (path: string, file: File) => uploadFileRaw(path, file),
  /** Server-side OpenAI TTS. Hands back the raw Response — see ttsText above. */
  ttsText,
  // Anthropic subscription-limit status per login profile (#426). Distinct from
  // `usage` below, which is per-agent cost/context telemetry.
  accountUsage: () =>
    usageGet<{
      profiles?: Array<{
        profile?: string;
        label?: string;
        ok?: boolean;
        error?: string | null;
        seven_day?: { utilization?: number | null; resets_at?: string | null } | null;
        five_hour?: { utilization?: number | null; resets_at?: string | null } | null;
        extra_usage?: {
          utilization?: number | null;
          is_enabled?: boolean;
          disabled_reason?: string | null;
        } | null;
      }>;
      // OpenAI/Codex accounts (Cloud 2b64ae5, additive — `profiles` unchanged).
      // Shape differs from the Anthropic block in three ways that matter:
      //   * `resets_at` is a UNIX EPOCH (seconds), not an ISO string
      //   * `credits.balance` is a STRING (or null), not a number
      //   * the data is a SNAPSHOT of unknown age — OpenAI has no usage GET, so
      //     Cloud reads the rate_limits that ride along on each Codex answer out
      //     of the session logs. `age_seconds` is therefore load-bearing, not
      //     decoration: values can be a day old.
      // The array can hold MORE THAN ONE account (Alex' bots hang on two ChatGPT
      // logins) — never sum them, never treat [0] as "the" account.
      codex?: Array<{
        email?: string;
        plan?: string;
        used_percent?: number | null;
        window_minutes?: number | null;
        resets_at?: number | null;
        credits?: { has_credits?: boolean; unlimited?: boolean; balance?: string | null } | null;
        rate_limit_reached?: unknown;
        age_seconds?: number | null;
        source_bot?: string | null;
      }>;
    }>('/api/account-usage'),
  usage: () =>
    get<{
      agents: Array<{ name: string; cost: number; context_pct: number; duration_minutes: number; context_tokens: number }>;
      totals: { cost: number; output_tokens: number };
    }>('/api/usage'),
  /** Per-agent MCP/login health incl. owner_email. Cloud gates this endpoint
   *  (#591) because it served 133 foreign e-mail addresses to the open internet;
   *  on 401 this throws and the caller's catch simply leaves the lock badge off. */
  mcpHealth: () =>
    get<{
      agents?: Array<{
        name: string;
        status: string;
        role: string | null;
        department: string | null;
        owner_email: string;
      }>;
    }>('/api/auth/mcp-health'),
  rbac: {
    config: () => get<RbacConfig>('/api/rbac/config'),
    assignments: () => get<{ assignments: RbacAssignment[] }>('/api/rbac/assignments'),
    // The three below went through raw fetch without any Authorization header
    // until #591 — assign/unassign MUTATE role bindings, so they are the last
    // calls that should have been unauthenticated.
    check: (session: string, permission: string) =>
      get<{ allowed: boolean; role: string; department: string }>(
        `/api/rbac/check?session=${encodeURIComponent(session)}&permission=${encodeURIComponent(permission)}`,
      ),
    assign: (session: string, role: string) =>
      post<unknown>('/api/rbac/assignments', { session, role }),
    unassign: (session: string) => del(`/api/rbac/assignments/${encodeURIComponent(session)}`),
    tenantMap: () => get<{
      tenants: { id: string; display_name: string; description?: string }[];
      users: { email: string; name: string; is_superadmin: boolean; tenants: string[] }[];
      agents: { name: string; owner_email: string | null; tenant_id: string | null; node: string }[];
    }>('/api/rbac/tenant-map'),
  },
  cluster: {
    nodes: () =>
      get<{
        local: string;
        nodes: Array<{ name: string; url: string; visibility: string; is_self: boolean }>;
      }>('/api/cluster/nodes'),
  },
  health: {
    summary: () => get<BotHealthResponse>('/api/sessions/health-summary'),
  },
  history: {
    // Long-running (5-15s) — caller passes AbortSignal to cancel.
    search: async (
      params: {
        q: string;
        expand?: boolean;
        limit?: number;
        agent?: string;
        owner?: string;
        whole_word?: boolean;
        federation?: boolean;
      },
      signal?: AbortSignal
    ): Promise<HistorySearchResponse> => {
      const qs = new URLSearchParams();
      qs.set('q', params.q);
      if (params.expand) qs.set('expand', 'true');
      if (params.limit != null) qs.set('limit', String(params.limit));
      if (params.agent) qs.set('agent', params.agent);
      if (params.owner) qs.set('owner', params.owner);
      if (params.whole_word === false) qs.set('whole_word', 'false');
      if (params.federation === false) qs.set('federation', 'false');
      const res = await fetch(`${BASE}/api/search/history?${qs.toString()}`, {
        headers: authHeader(),
        signal,
      });
      if (!res.ok) {
        const msg = await readErrorBody(res);
        throw new Error(msg || `API ${res.status}: search/history`);
      }
      return res.json();
    },
  },
};

// Voice talk-back gate (Step 2, #1220) — the deterministic Cloud guard
// (voice_gate.py). Browser registers focus on every switch, submits a single
// propose_to_agent command, and the gate auto-relays (agent + safe) or asks for
// confirmation. The branch field is `gate` (auto_ok | confirm_required | denied),
// `policy_class` is the classification. These return the body even on 409/403 so
// the UI can show the `gate`/`reason` (so NOT via the throwing `post` helper).
export interface ProposalResult {
  status: number;
  proposal_id?: string;
  gate?: 'auto_ok' | 'confirm_required' | 'denied';
  policy_class?: string;
  relay_sent?: boolean;
  decision?: string;
  send_preview?: string;
  audit_id?: string;
  reason?: string;
  error?: string;
  detail?: string | Array<{ msg?: string }>;
}

// Arcturian's browser executor. Identity is deliberately absent from every
// request type: cloud-api derives owner/tenant authority exclusively from the
// verified user JWT. The browser may select an already-issued grant, but it can
// neither mint authority nor claim another principal (#977).
export interface ArcturianAuthorityGrant {
  grant_id: string;
  revision: number;
  principal_user_id: string;
  tenant_id: string | null;
  scopes: string[];
  targets: string[];
  modes: string[];
  expires_at: number | null;
  revoked_at: number | null;
  updated_at: number;
}

export interface ArcturianAuthorityTargets {
  protocol: string;
  principal_user_id: string;
  tenant_id: string | null;
  scope: string;
  mode: string;
  targets: string[];
}

export interface ArcturianActionReceipt {
  protocol: string;
  action_id: string;
  task_id: string | null;
  conversation_id: string;
  message_id: string;
  principal_user_id: string;
  tenant_id: string | null;
  revision: number;
  state: string;
  delivered_at: number | null;
  authority_ref: { grant_id: string; revision: number; class?: string };
}

export interface ArcturianActionRecord {
  protocol: string;
  action_id: string;
  task_id: string | null;
  conversation_id: string;
  message_id: string;
  principal_user_id: string;
  tenant_id: string | null;
  target_session: string;
  channel: string;
  content: string;
  authority_ref: { grant_id: string; revision: number; class?: string };
  state: string;
  revision: number;
  delivered_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface ArcturianExecuteResponse {
  action: ArcturianActionRecord;
  receipt: ArcturianActionReceipt;
  duplicate: boolean;
}

export interface ArcturianConversationEvent {
  event_id: string;
  conversation_id: string;
  action_id: string | null;
  task_id: string | null;
  correlation_id: string;
  revision: number;
  direction: 'inbound' | 'outbound';
  actor: string;
  event_type: string;
  content: string;
  artifacts: unknown[];
  created_at: number;
  principal_user_id: string;
  tenant_id: string | null;
}

export interface ArcturianConversationEventsResponse {
  conversation_id: string;
  events: ArcturianConversationEvent[];
  next_revision: number;
}

export interface ArcturianEventPresentation {
  conversation_id: string;
  event_id: string;
  device_id: string;
  status: 'pending' | 'presented' | 'consumed';
  updated_at: number;
  superseded?: boolean;
  principal_user_id: string;
  tenant_id: string | null;
}
async function arcturianRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const doFetch = () => fetch(path, {
    ...init,
    headers: {
      'X-Client-Build': __BUILD_ID__,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
      ...(init?.headers || {}),
    },
  });
  let res = await doFetch();
  if (res.status === 401 && await refreshAccessToken()) res = await doFetch();
  if (!res.ok) {
    const message = await readErrorBody(res);
    throw new Error(message || `Arcturian API ${res.status}: ${path}`);
  }
  return jsonOrExplain<T>(res, path);
}

export const arcturianActionApi = {
  // Calling target discovery first is intentional: cloud-api establishes the
  // owner's implicit baseline grant on this path before the client lists it.
  authorityTargets: (): Promise<ArcturianAuthorityTargets> =>
    arcturianRequest('/api/arcturian/authority-targets?scope=internal_message&mode=direct_iacp'),

  grants: async (): Promise<ArcturianAuthorityGrant[]> => {
    const result = await arcturianRequest<{ grants: ArcturianAuthorityGrant[] }>(
      '/api/arcturian/authority-grants',
    );
    return result.grants;
  },

  list: async (): Promise<ArcturianActionRecord[]> => {
    const result = await arcturianRequest<{ actions: ArcturianActionRecord[] }>(
      '/api/arcturian/actions',
    );
    return result.actions;
  },

  execute: (body: {
    target: string;
    channel: 'direct_iacp';
    content: string;
    kind: string;
    artifacts: unknown[];
    response_contract: Record<string, unknown>;
    authority_ref: { grant_id: string; revision: number };
    conversation_id?: string;
  }, idempotencyKey: string): Promise<ArcturianExecuteResponse> =>
    arcturianRequest('/api/arcturian/actions', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(body),
    }),
};

/** Read-only owner-scoped return wire. Text retention and writer leases remain
 * deliberately outside this release; identity comes exclusively from JWT. */
export const arcturianReturnApi = {
  listEvents: (
    conversationId: string,
    afterRevision: number,
    limit = 100,
    tail = 0,
  ): Promise<ArcturianConversationEventsResponse> => {
    const query = new URLSearchParams({
      after_revision: String(afterRevision),
      limit: String(limit),
    });
    if (tail > 0) query.set('tail', String(tail));
    return arcturianRequest(
      `/api/arcturian/conversations/${encodeURIComponent(conversationId)}/events?${query.toString()}`,
    );
  },

  acknowledgePresentation: (
    conversationId: string,
    eventId: string,
    body: { device_id: string; status: 'pending' | 'presented' | 'consumed' },
  ): Promise<{ presentation: ArcturianEventPresentation }> =>
    arcturianRequest(
      `/api/arcturian/conversations/${encodeURIComponent(conversationId)}/events/${encodeURIComponent(eventId)}/presentation`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
};
export const voiceGate = {
  // Mandatory before any proposal (else 409 no_focus_registered).
  registerFocus: async (voiceSessionId: string, epoch: number, target: string, context = 'agent'): Promise<boolean> => {
    try {
      // The endpoint requires `focus_epoch` + `target_session` (NOT `epoch`/`target`
      // — verified live 2026-06-24: wrong names → 400 → proposal `no_focus_registered`).
      const res = await fetch('/api/voice/realtime/focus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ voice_session_id: voiceSessionId, focus_epoch: epoch, target_session: target, context }),
      });
      return res.ok;
    } catch {
      return false;
    }
  },
  submitProposal: async (p: {
    voiceSessionId: string;
    focusEpoch: number;
    targetSession: string;
    proposedText: string;
    targetContext?: string;
  }): Promise<ProposalResult> => {
    try {
      const res = await fetch('/api/voice/realtime/proposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          voice_session_id: p.voiceSessionId,
          focus_epoch: p.focusEpoch,
          target_session: p.targetSession,
          proposed_text: p.proposedText,
          target_context: p.targetContext || 'agent',
        }),
      });
      const body = await res.json().catch(() => ({}));
      return { status: res.status, ...body };
    } catch {
      return { status: 0, gate: 'denied', reason: 'network_error' };
    }
  },
  confirmProposal: async (proposalId: string): Promise<ProposalResult> => {
    try {
      const res = await fetch(`/api/voice/realtime/proposals/${encodeURIComponent(proposalId)}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        // cloud-api declares the body as a required FastAPI parameter. Without
        // an explicit decision the request is rejected with 422 before the
        // route's fallback can run (#965).
        body: JSON.stringify({ decision: 'approve' }),
      });
      const body = await res.json().catch(() => ({}));
      return { status: res.status, ...body };
    } catch {
      return { status: 0, gate: 'denied', reason: 'network_error' };
    }
  },
  // Transparent-mode relay (agent-transparent, post #1235). Same deterministic
  // gate-core as proposals (focus-stale + RBAC) but NO human confirm-loop —
  // fire-and-acknowledge. `pair_id` is echoed on EVERY path (auto_ok + denied) so
  // the agent_voice_response threading works even on rejection. denied = visible,
  // never silent-drop. Cloud: 200 auto_ok | 409 focus_stale | 403 not_authorized.
  relayToAgent: async (p: {
    voiceSessionId: string;
    focusEpoch: number;
    targetSession: string;
    text: string;
    pairId: string;
  }): Promise<{ status: number; gate?: string; relay_sent?: boolean; relay_id?: string; pair_id?: string; send_preview?: string; reason?: string }> => {
    try {
      const res = await fetch('/api/voice/realtime/relays', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          voice_session_id: p.voiceSessionId,
          focus_epoch: p.focusEpoch,
          target_session: p.targetSession,
          text: p.text,
          pair_id: p.pairId,
        }),
      });
      const body = await res.json().catch(() => ({}));
      return { status: res.status, ...body };
    } catch {
      return { status: 0, gate: 'denied', reason: 'network_error' };
    }
  },
};
